import { IAssigneesStoreState, IAssigneeIdMap, AssigneeActions } from "@gtmhub/assignees";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import dayjs from "@gtmhub/dayjs";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { CurrentUserService, getCurrentUserId, UserService } from "@gtmhub/users";
import { IScope, IController, IQService } from "angular";
import { HierarchyNode } from "d3";
import { IPlanningSession, IStratifiedPlanningSession } from "../models/models";
import { PlanningSessionsActions } from "../redux/session-actions";
import { ISessionsStoreState } from "../redux/session-reducer";
import { filterActiveSessionsWithoutCheckDate } from "../redux/session-selectors";
import { DeleteSessionConfirmationService } from "../services/delete-session-confirmation-service";
import { calculateSessionsTimeframe, createSessionTree, generateLinesInSessionsGrid, generateTimePeriods } from "./utils";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { allowedActionsSync } from "@gtmhub/permissions/utils";

const bindStateToScope = (state: ISessionsStoreState & IAssigneesStoreState): IPlanningSessionsRedux => ({
  allSessions: state.sessions.items,
  allActiveSessions: filterActiveSessionsWithoutCheckDate(state.sessions.items),
  indicators: {
    loading: state.sessions.isFetching,
    deleting: state.sessions.isDeleting,
  },
  assignees: state.assignees.map,
  isSessionDeleted: state.sessions.isDeleted,
  deleteSessionError: state.sessions.deleteSessionError,
});

interface IPlanningSessionsRedux {
  allSessions: IPlanningSession[];
  allActiveSessions: IPlanningSession[];
  indicators: {
    loading: boolean;
    deleting: boolean;
  };
  assignees: IAssigneeIdMap;
  isSessionDeleted: boolean;
  deleteSessionError: IUIError;
}

interface IAllowedActionsForSession {
  [sessionId: string]: string[];
}

export interface IPlanningSessionsScope extends IScope, IExtendedRootScopeService, IPlanningSessionsRedux {
  timeframe: number;
  lines: string[];
  timePeriods: Record<string, string[]>;
  timeframeLinesCount: number;
  todaysDate: {
    date: string;
    leftPercentage: number;
  };
  allowedActionsPerSession: IAllowedActionsForSession;
  showAllSessions: boolean;
  sessions: HierarchyNode<IPlanningSession>[];

  getCurrentUserAllowedActions(session: IPlanningSession): void;
  confirmDeletePlanningSession(planningSession: IPlanningSession): void;
  confirmDeleteDemoSession(): void;
  onToggleSessions(): void;
  favoritesManager: FavoritesManager;
  getNavItem(list: IPlanningSession): INavItem;
  sessionIsFavorite(list: IPlanningSession): boolean;
  isNewExperienceEnabled(): boolean;
}

export class PlanningSessionsCtrl implements IController {
  private firstLoadOfCtrl: boolean;

  private checkingEnabledFeatures: boolean;
  private showNewExperience: boolean;
  private favoritesManager: FavoritesManager;

  public static $inject = [
    "$rootScope",
    "$scope",
    "UIErrorHandlingService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
    "DeleteSessionConfirmationService",
    "CurrentUserService",
    "AnalyticsService",
    "FeatureToggleService",
    "FavoritesManagerFactory",
    "AccountService",
    "UserService",
    "$q",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IPlanningSessionsScope,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private deleteSessionConfirmationService: DeleteSessionConfirmationService,
    private currentUserService: CurrentUserService,
    private analyticsService: AnalyticsService,
    private featureToggleService: FeatureToggleService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private accountService: AccountService,
    private userService: UserService,
    private $q: IQService
  ) {
    this.$rootScope.noNavigation = false;
    this.$scope.allSessions = [];
    this.$scope.allowedActionsPerSession = {};
    this.$scope.timeframeLinesCount = 12;
    this.$scope.showAllSessions = this.currentUserService.getUserSetting<boolean>("showArchivedSessions") || false;
    this.firstLoadOfCtrl = true;
    this.favoritesManager = this.favoritesManagerFactory(["session"]);

    this.$scope.confirmDeletePlanningSession = this.confirmDeletePlanningSession;
    this.$scope.confirmDeleteDemoSession = this.confirmDeleteDemoSession;
    this.$scope.onToggleSessions = this.onToggleSessions;

    this.$scope.$watch("allSessions", () => this.processSessions(this.$scope.showAllSessions));

    this.$scope.favoritesManager = this.favoritesManager;
    this.$scope.getNavItem = this.getNavItem;
    this.$scope.sessionIsFavorite = this.sessionIsFavorite;
    this.$scope.isNewExperienceEnabled = this.isNewExperienceEnabled;

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
  }

  private processSessions = (showAllSessions: boolean) => {
    const sessions: IPlanningSession[] = !showAllSessions ? this.$scope.allActiveSessions : this.$scope.allSessions;
    let ganttMode = false;
    this.$scope.sessions = createSessionTree(sessions)?.children || [];

    if (!sessions.length) {
      return;
    }

    this.trackSessionsPageDataLoaded(sessions, showAllSessions);

    this.checkingEnabledFeatures = true;
    this.showNewExperience = false;

    const promises = {
      newExperienceEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperience),
      newExperienceToggleEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NewExperienceToggle),
      gantModeEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.AllSessionsGanntMode),
      currentUser: this.userService.getUser(getCurrentUserId()),
    };

    this.$q
      .all(promises)
      .then(({ newExperienceEnabled, newExperienceToggleEnabled, gantModeEnabled, currentUser }) => {
        const newExperienceAccountSettingEnabled = this.accountService.getAccountSetting<Record<"enabled", boolean>>("newExperience")?.enabled;
        this.showNewExperience = newExperienceEnabled && newExperienceToggleEnabled && newExperienceAccountSettingEnabled;
        this.checkingEnabledFeatures = false;
        if (this.showNewExperience) {
          this.favoritesManager.initFavorites();
        }

        ganttMode = gantModeEnabled;

        sessions.forEach((session) => {
          this.$scope.allowedActionsPerSession[session.id] = allowedActionsSync(currentUser, session.access);
        });
      })
      .finally(() => {
        const { startDate, endDate, timeframe } = calculateSessionsTimeframe(sessions, ganttMode);
        // calculate what part is a day of timeframe
        const dayPartInTimeframe = 100 / timeframe;

        for (let i = 0; i < sessions.length; i++) {
          const daysLength = dayjs(sessions[i].end).diff(sessions[i].start, "days");
          sessions[i].daysLength = daysLength;
          sessions[i].lengthPercentage = dayPartInTimeframe * daysLength;
          sessions[i].leftPercentage = dayPartInTimeframe * dayjs(sessions[i].start).diff(startDate, "days");
        }

        this.$scope.lines = generateLinesInSessionsGrid(startDate, timeframe, this.$scope.timeframeLinesCount);
        this.$scope.timePeriods = generateTimePeriods(startDate, endDate);
        this.$scope.timeframe = timeframe;
        this.$scope.todaysDate = {
          date: dayjs().toISOString(),
          leftPercentage: dayPartInTimeframe * dayjs().diff(startDate, "days"),
        };
      })
      .catch(() => (this.checkingEnabledFeatures = false));
  };

  private confirmDeleteDemoSession = () => {
    this.deleteSessionConfirmationService.confirmDeleteDemoSession();
  };

  private confirmDeletePlanningSession = (planningSession: IStratifiedPlanningSession) => {
    this.deleteSessionConfirmationService.confirmDeletePlanningSession(planningSession.data, () => {
      this.deletePlanningSession(planningSession.id);
    });
  };

  private deletePlanningSession = (planningSessionId: string) => {
    this.$ngRedux.dispatch(this.planningSessionsActions.deletePlanningSession(planningSessionId));
    this.onSessionDeleted();
  };

  private onSessionDeleted = () => {
    this.$scope.watchUntil(() => this.$scope.deleteSessionError).then(() => this.uiErrorHandlingService.handleModal(this.$scope.deleteSessionError));
  };

  private onToggleSessions = () => {
    this.$scope.showAllSessions = !this.$scope.showAllSessions;
    this.currentUserService.setUserSetting({ showArchivedSessions: this.$scope.showAllSessions });

    const trackEventMeta = { name: "show_archived_sessions", state: this.$scope.showAllSessions ? "enabled" : "disabled" };
    this.analyticsService.track("Toggle Set", trackEventMeta);

    this.processSessions(this.$scope.showAllSessions);
  };

  private trackSessionsPageDataLoaded = (sessions: IPlanningSession[], isArchivedSessionsVisible: boolean) => {
    if (this.firstLoadOfCtrl) {
      this.analyticsService.track("Sessions Page Data Loaded", { sessions: sessions.length, is_archived_sessions_visible: isArchivedSessionsVisible });
      this.firstLoadOfCtrl = false;
    }
  };

  private getNavItem = (session: IPlanningSession): INavItem => {
    return this.favoritesManager.getFavoriteItem({ id: session.id }) || navItemsFactory.buildSessionNavItem(session);
  };

  private sessionIsFavorite = (session: IPlanningSession): boolean => {
    if (!this.isNewExperienceEnabled()) return false;
    return this.favoritesManager.entityIsFavorite({ id: session.id });
  };

  private isNewExperienceEnabled = (): boolean => {
    return !this.checkingEnabledFeatures && this.showNewExperience;
  };
}
